import {
  ChangeEvent,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  createStyles,
  Divider,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  Equalizer as EqualizerIcon,
  InfoOutlined as InfoOutlinedIcon,
  InsertDriveFile as InsertDriveFileIcon,
  QueueMusic as QueueMusicIcon,
  Subscriptions as SubscriptionsIcon,
} from "@material-ui/icons";
import { head, intersection, throttle, uniq } from "lodash";
import { usePostHog } from "posthog-js/react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router";
import scroll, { Element, Link } from "react-scroll";

import { ErrorBoundary, ExchangeNotificationSnackBar } from "components";
import { getThumbnailUrlFromMediaUrl } from "components/MediaPlayer";
import { BaseContainer } from "containers";
import { AppContext } from "contexts";
import { useExchangeNotifications } from "services/exchangeNotifications";
import { RoyaltyPayorKind } from "services/royaltyPayor/types";
import { createListingViewedByUser } from "services/viewedListings";
import theme from "theme";
import {
  assetDetailsTabHeight,
  desktopHeaderHeight,
  headerHideTransition,
  mobileHeaderHeight,
} from "theme/constants";

import About from "./components/About";
import AssetDetailHeader from "./components/AssetDetailHeader";
import ErrorBoundaryCustomContainer from "./components/ErrorBoundaryCustomContainer";
import Financials from "./components/Financials";
import Media from "./components/Media";
import Royalties from "./components/Royalties/Royalties";
import Tracks from "./components/Tracks";
import { useAssetDetailData } from "./hooks";

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    tabsContainer: {
      fontSize: "2rem",
      height: `${assetDetailsTabHeight}px`,
      width: "100%",
      backgroundColor: theme.custom.palette.gray.main,
      boxShadow: theme.shadows[3],
      color: theme.custom.palette.gray.dark,
      transition: headerHideTransition,
      top: 0,
      zIndex: 100,
      [theme.breakpoints.up("sm")]: {
        "nav.nav.-open ~ #react-root & ": {
          top: `${desktopHeaderHeight}px`,
        },
      },
      [theme.breakpoints.up("xs")]: {
        "nav.navigation.-open ~ #react-root & ": {
          top: `${mobileHeaderHeight}px`,
        },
      },
    },
    tab: {
      flexGrow: 1,
      maxWidth: "unset",
      minWidth: "unset",
      fontWeight: theme.typography.fontWeightBold,
      textTransform: "unset",
      position: "relative",
      "&:hover": {
        color: theme.custom.palette.purple.main,
        opacity: 1,
      },
      "&::before": {
        content: "''",
        position: "absolute",
        top: "50%",
        height: "50%",
        right: "0px",
        width: "0.5px",
        transform: "translateY(-50%)",
        backgroundColor: theme.custom.palette.gray.dark,
      },
      "&:last-child::before": {
        display: "none",
      },
      "&::after": {
        content: "''",
        position: "absolute",
        top: "50%",
        height: "50%",
        left: "0px",
        width: "0.5px",
        transform: "translateY(-50%)",
        backgroundColor: theme.custom.palette.gray.dark,
      },
      "&:first-child::after": {
        display: "none",
      },
      "&.Mui-selected": {
        backgroundColor: theme.custom.palette.purple.main,
        color: theme.palette.primary.contrastText,
        border: "none",
      },
    },
    tabLabel: {
      display: "flex",
      alignItems: "center",
    },
  }),
);

const scrollDuration = 300;
const sectionNames = [
  "About",
  "Financials",
  "Royalties",
  "Media",
  "Included Tracks",
] as const;
type SectionName = typeof sectionNames[number];
type Section = {
  icon: ReactElement;
  content: ReactElement;
  displayTitleWithContent: boolean;
};

type AssetDetailModalProps = {
  listingId: number;
};
interface AssetDetailProps {
  isModal?: AssetDetailModalProps;
}

const AssetDetail: FC<AssetDetailProps> = ({ isModal }) => {
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });

  const { navHeight } = useContext(AppContext);
  const { user } = useContext(AppContext);
  const classes = useStyles({ assetDetailsTabHeight });

  const posthog = usePostHog();

  const queryParams = useParams<{
    listingId: string;
  }>();

  const listingId = isModal
    ? isModal.listingId
    : queryParams.listingId
    ? Number(queryParams.listingId)
    : 0;

  const {
    asset,
    listing,
    valuation,
    valuationDescription,
    isLoading,
    isError,
  } = useAssetDetailData(Number(listingId));

  const { data: exchangeNotification } = useExchangeNotifications(
    {
      filter: { kind: ["asset_details_page"] },
    },
    !!user,
  );

  useEffect(() => {
    if (user?.userId && listing?.id) {
      createListingViewedByUser(listing.id);
    }
  }, [listing?.id, user?.userId]);

  const hasCustomIncludedRights = !!listing?.included_rights;
  const royaltyPayorKindsWithoutDefaultIncludedRights: RoyaltyPayorKind[] = [
    "other",
    "custom",
  ];
  const royaltyPayorKinds = uniq(
    asset?.royalty_payors?.map(royaltyPayor => royaltyPayor.kind) || [],
  );
  const canShowDefaultIncludedRights =
    royaltyPayorKinds.length === 1 &&
    !intersection(
      royaltyPayorKindsWithoutDefaultIncludedRights,
      royaltyPayorKinds,
    ).length;

  const canDisplayRightsSection =
    hasCustomIncludedRights || canShowDefaultIncludedRights;

  const isLoadingOrError = isLoading || isError;

  const [sectionIndex, setSectionIndex] = useState(0);
  const throttledSetSectionIndex = useMemo(
    () => throttle(setSectionIndex, scrollDuration + 150, { trailing: true }),
    [],
  );
  const activeSectionNames: SectionName[] = sectionNames.filter(
    name =>
      !(name === "Media" && !listing?.media_urls.length) &&
      !(name === "Royalties" && !canDisplayRightsSection),
  );

  const handleSectionChange = (
    _: ChangeEvent<unknown>,
    updatedSectionIndex: number,
  ) => {
    const isScrollingUp = updatedSectionIndex < sectionIndex;
    const navOffset =
      isScrollingUp && !isModal
        ? isMdUp
          ? desktopHeaderHeight
          : mobileHeaderHeight
        : 0;

    throttledSetSectionIndex(updatedSectionIndex);
    scroll.scroller.scrollTo(activeSectionNames[updatedSectionIndex], {
      duration: scrollDuration,
      smooth: true,
      offset: -assetDetailsTabHeight - navOffset,
      containerId: !!isModal ? "containerElement" : undefined,
    });
  };

  const sections: Record<SectionName, Section> = {
    About: {
      icon: <InfoOutlinedIcon />,
      content: !isLoadingOrError && !!valuation && (
        <About listingId={listingId} isModal={!!isModal} />
      ),
      displayTitleWithContent: false,
    },
    Financials: {
      icon: <EqualizerIcon />,
      content: !!valuation && (
        <Financials
          asset={asset}
          isModal={!!isModal}
          listing={listing}
          valuation={valuation}
          valuationDescription={valuationDescription}
        />
      ),
      displayTitleWithContent: true,
    },
    Royalties: {
      icon: <InsertDriveFileIcon />,
      content: (
        <Royalties
          customIncludedRights={listing?.included_rights}
          royaltyPayors={asset?.royalty_payors}
        />
      ),
      displayTitleWithContent: true,
    },
    Media: {
      icon: <SubscriptionsIcon />,
      content: <Media mediaUrls={listing?.media_urls} />,
      displayTitleWithContent: true,
    },
    "Included Tracks": {
      icon: <QueueMusicIcon />,
      content: (
        <Tracks
          listingId={listing?.id}
          tracks={valuation?.track_list || []}
          isTrackListHidden={listing?.is_track_list_hidden}
        />
      ),
      displayTitleWithContent: true,
    },
  };

  const ogImage =
    !isLoadingOrError &&
    [listing?.thumbnail_large, ...(listing?.media_urls || [])]
      .map((mediaUrl, index) => {
        const ogImageUrl = getThumbnailUrlFromMediaUrl(mediaUrl) || mediaUrl;
        return ogImageUrl
          ? {
              key: index,
              name: "image",
              property: "og:image",
              content: ogImageUrl,
            }
          : null;
      })
      .filter(Boolean);

  const twitterImageUrl = listing?.thumbnail_large
    ? listing?.thumbnail_large
    : head(listing?.media_urls)
    ? getThumbnailUrlFromMediaUrl(listing?.media_urls[0])
    : null;

  const twitterMeta = twitterImageUrl
    ? [{ name: "twitter:image", content: twitterImageUrl }]
    : [];

  return (
    <ErrorBoundary
      posthog={posthog}
      CustomContainer={ErrorBoundaryCustomContainer}
      scrollTop={!isModal}
    >
      {exchangeNotification && (
        <ExchangeNotificationSnackBar
          key={exchangeNotification?.title}
          title={exchangeNotification.title}
          message={exchangeNotification.message}
        />
      )}
      <div
        id="containerElement"
        style={{ overflow: !!isModal ? "auto" : "unset", width: "100%" }}
      >
        {!!listing && (
          <Helmet
            title={`${
              listing.title ? listing.title : `Listing #${listing.id}`
            } | Royalty Exchange`}
            meta={[
              {
                name: "title",
                property: "og:title",
                content: `${
                  listing.title ? listing.title : `Listing #${listing.id}`
                } | Royalty Exchange`,
              },
              { name: "twitter:card", content: "summary_large_image" },
              {
                name: "twitter:title",
                content: `${
                  listing.title ? listing.title : `Listing #${listing.id}`
                } | Royalty Exchange`,
              },
              ...(ogImage || []),
              ...twitterMeta,
            ]}
          />
        )}
        <BaseContainer
          renderFullWidthContent={() => (
            <>
              {/* the Element and Link components here "stretch" the 0th section so that scrolling while above the 0th section still triggers selection of the 0th section */}
              <Element name="header">
                <AssetDetailHeader
                  isModal={!!isModal}
                  isLoading={isLoadingOrError}
                  listingId={listingId}
                />
              </Element>
              <Link
                spy
                to="header"
                onSetActive={() => {
                  throttledSetSectionIndex(0);
                }}
                containerId={!!isModal ? "containerElement" : undefined}
              />
              {activeSectionNames.map((section, index) => {
                return (
                  <Link
                    spy
                    key={section}
                    to={section}
                    onSetActive={() => throttledSetSectionIndex(index)}
                    offset={-assetDetailsTabHeight - navHeight}
                    containerId={!!isModal ? "containerElement" : undefined}
                  />
                );
              })}
              <Box position="sticky" className={classes.tabsContainer}>
                <Tabs
                  value={sectionIndex}
                  onChange={handleSectionChange}
                  TabIndicatorProps={{
                    style: { display: "none" },
                  }}
                >
                  {activeSectionNames.map(section => (
                    <Tab
                      wrapped={!!isModal}
                      key={section}
                      label={
                        isXsDown ? (
                          sections[section].icon
                        ) : (
                          <span className={classes.tabLabel}>
                            {sections[section].icon}&nbsp;&nbsp;{section}
                          </span>
                        )
                      }
                      className={classes.tab}
                    />
                  ))}
                </Tabs>
              </Box>
            </>
          )}
        >
          {!isLoadingOrError &&
            activeSectionNames.map(sectionName => {
              const section = sections[sectionName];
              return (
                <Box key={sectionName}>
                  <Element className={classes.section} name={sectionName}>
                    {section.displayTitleWithContent && (
                      <Box mb={4}>
                        <Typography variant="h1">{sectionName}</Typography>
                      </Box>
                    )}
                    {section.content}
                  </Element>
                  <Divider />
                </Box>
              );
            })}
        </BaseContainer>
      </div>
    </ErrorBoundary>
  );
};

export default AssetDetail;
